import * as React from 'react'

import {
  UserListItem,
  UserListRequestPayload,
  useMutationRemoveAdminPanelUsers,
} from '@/lib/api/company/configuration/adminPanel/users'
import { TableProvider, useTable } from '@/modules/drivers/context/TableContext'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { AdminIcon, CheckboxField } from '@/modules/shared/components'
import {
  clearAdminSelection,
  setAdminSelectedCount,
  subscribeToAdminRemove,
} from '@/modules/shared/stores/adminSelectionStore'
import { toast } from '@/lib/toasts/api'
import { AdminUsersTable } from './components/AdminUsersTable'

type Props = {
  /** Pusty = widok globalny (wszystkie firmy). */
  companyId?: string
  onEditUser: (user: UserListItem) => void
}

const PER_PAGE_DEFAULT = 10

/**
 * Zakładka UŻYTKOWNICY panelu admina - funkcyjna, na react-query i komponentach z `/et`.
 *
 * Zastępuje `app/components/administration/users.tsx` (klasa, react-table v6, imperatywne
 * `app/actions`). Zaznaczenie idzie przez `TableContext`, a jego liczność jest przekazywana
 * do `adminSelectionStore`, żeby pasek z koszem na dole panelu działał tak jak w pozostałych
 * zakładkach.
 */
const AdminUsersPageContent: React.FC<Props> = ({ companyId, onEditUser }) => {
  const { t } = useLocalize()
  const { selectedGroup, resetAll } = useTable()
  const mutationRemove = useMutationRemoveAdminPanelUsers(companyId)

  const [filters, setFilters] = React.useState<UserListRequestPayload>({
    take: PER_PAGE_DEFAULT,
    skip: 0,
    orderBy: 'createdDate',
    descending: true,
  })
  const [search, setSearch] = React.useState('')

  // Wyszukiwarka odpytuje po chwili bezruchu - stary panel miał `DelayInput` z 500 ms.
  React.useEffect(() => {
    const timer = setTimeout(
      () => setFilters((prev) => ({ ...prev, searchValue: search, skip: 0 })),
      500,
    )
    return () => clearTimeout(timer)
  }, [search])

  const selectedCount = selectedGroup?.ids.length ?? 0
  React.useEffect(() => {
    setAdminSelectedCount(selectedCount)
  }, [selectedCount])
  React.useEffect(() => () => clearAdminSelection(), [])

  // Kosz w pasku panelu ogłasza usunięcie przez store - tak samo jak w pozostałych zakładkach.
  const selectedRef = React.useRef<Array<string | number>>([])
  selectedRef.current = selectedGroup?.ids ?? []
  React.useEffect(
    () =>
      subscribeToAdminRemove(async () => {
        const ids = selectedRef.current
        if (ids.length === 0) return
        try {
          await mutationRemove.mutateAsync({ userIds: ids })
          toast.success({
            title: t('administrationPanel-users.users-deleted-title'),
          })
        } catch {
          toast.error({ title: t('common.error') })
        }
        resetAll()
        clearAdminSelection()
      }),
    [mutationRemove, resetAll, t],
  )

  return (
    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-bg-content-primary">
      <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-6 tw-border-b tw-border-base-300 tw-px-4 tw-py-2">
        <div className="tw-flex tw-items-center tw-gap-2">
          <AdminIcon name="search" />
          <input
            className="tw-w-[280px] tw-border-0 tw-bg-transparent tw-text-sm tw-outline-none"
            placeholder={t('common.search')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <CheckboxField
          id="admin-users-only-blocked"
          label={t('administrationPanel-users.only-blocked')}
          checked={filters.onlyBlocked ?? false}
          onChange={(event) =>
            setFilters((prev) => ({
              ...prev,
              onlyBlocked: event.target.checked,
              skip: 0,
            }))
          }
        />
      </div>

      <AdminUsersTable
        companyId={companyId}
        onEdit={onEditUser}
        filters={filters}
        setFilters={setFilters}
      />
    </div>
  )
}

export const AdminUsersPage: React.FC<Props> = (props) => (
  <TableProvider>
    <AdminUsersPageContent {...props} />
  </TableProvider>
)
